import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  DollarSign, 
  TrendingUp, 
  Activity, 
  Image as ImageIcon,
  MessageSquare,
  Calendar,
  Filter,
  BarChart3,
  PieChart,
  RefreshCw,
  Download,
  AlertTriangle,
  CheckCircle,
  CreditCard,
  Plus,
  X,
  Bot,
  Zap,
  Users,
  Shield
} from 'lucide-react';
import { hasPageAccess } from '../utils/permissions';

const AICosts = () => {
  const { user, token, loading: authLoading, isAdmin, API_BASE_URL } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [usageLogs, setUsageLogs] = useState([]);
  const [userUsage, setUserUsage] = useState([]);
  const [providerUsage, setProviderUsage] = useState([]);
  const [modelUsage, setModelUsage] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [aiSettings, setAiSettings] = useState(null);
  const [purchaseAmount, setPurchaseAmount] = useState(10);
  const [purchasing, setPurchasing] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [refreshingPending, setRefreshingPending] = useState(false);

  const periods = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'year', label: 'This Year' }
  ];

  // Helper function to convert text to Title Case
  const toTitleCase = (str) => {
    return str.replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  };

  useEffect(() => {
    if (authLoading) {
      // Still loading authentication, don't do anything yet
      return;
    }
    
    if (!user) {
      setMessage({ type: 'error', text: 'Please log in to view AI costs.' });
      setPageLoading(false);
      return;
    }
    
    // Clear any previous error messages and fetch data
    setMessage({ type: '', text: '' });
    fetchCostData();
  }, [user, authLoading, selectedPeriod]);

  const fetchCostData = async () => {
    setLoading(true);
    try {
      const timestamp = new Date().getTime();
      
      // SINGLE ENDPOINT: Fetch all analytics data from generation tracking service (v2 supports OpenAI data switching)
      const analyticsResponse = await fetch(`${API_BASE_URL}/api/openrouter/enhanced-analytics-v2?period=${selectedPeriod}&t=${timestamp}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!analyticsResponse.ok) {
        const errorText = await analyticsResponse.text();
        console.error('Analytics API error:', analyticsResponse.status, errorText);
        throw new Error(`Failed to fetch analytics: ${analyticsResponse.status} ${errorText}`);
      }

      const analyticsData = await analyticsResponse.json();
      console.log('Analytics response:', analyticsData);
      
      if (analyticsData.success) {
          // Map the main summary data (balance, costs by provider, etc.)
          const summary = analyticsData.activity_summary || {};
          const providerBreakdown = analyticsData.provider_breakdown || [];
          const modelBreakdown = analyticsData.model_breakdown || [];
          const realTimeBalance = analyticsData.real_time_balance || {};
          
          console.log('Analytics data received:', analyticsData);
          console.log('Summary data:', summary);
          console.log('Image generation tokens from summary:', summary.image_generation_tokens);
          console.log('Image generation tokens in analytics:', analyticsData?.image_generation_tokens);
          
          // Use the text_generation_cost and image_generation_cost from summary
          const textGenerationCost = summary.text_generation_cost || 0;
          const imageGenerationCost = summary.image_generation_cost || 0;
          
          // Set analytics data in the format expected by existing components
          setAnalytics({
            total_cost: summary.total_cost || 0,
            monthly_cost: summary.total_cost || 0, 
            total_tokens: summary.total_tokens || 0,
            total_messages: summary.total_requests || 0,
            total_requests: summary.total_requests || 0,
            credits_balance: realTimeBalance?.success ? parseFloat(realTimeBalance.balance || 0) : 0,
            // Add specific costs for summary cards
            text_generation_cost: textGenerationCost,
            image_generation_cost: imageGenerationCost,
            // Add pending 404 count for refresh button
            pending_404_count: analyticsData.pending_404_count || summary.pending_404_count || 0,
            // Add image generation tokens for debugging
            image_generation_tokens: summary.image_generation_tokens || 0,
            // Convert new format to old format for existing components
            provider_breakdown: providerBreakdown.map(provider => ({
              _id: provider.provider,
              cost: provider.total_cost,
              requests: provider.total_requests,
              tokens: provider.total_tokens
            })),
            model_breakdown: modelBreakdown.map(model => ({
              _id: model.model,
              cost: model.total_cost,
              requests: model.total_requests,
              tokens: model.total_tokens,
              provider: model.provider
            })),
            daily_trends: [] // Could be implemented later with daily data
          });

          // Set provider and model breakdown from main analytics
          setProviderUsage(providerBreakdown);
          setModelUsage(modelBreakdown);
          
          // Process user breakdown data from single endpoint
          const userBreakdown = analyticsData.user_breakdown || [];
          
          // Format user names by cleaning app prefixes and fix data mapping
          const formattedUserBreakdown = userBreakdown.map(user => ({
            ...user,
            name: cleanUserName(user.name || 'Unknown User'),
            // Ensure all values are properly set
            total_tokens: user.total_tokens || 0,
            total_prompt_tokens: user.total_prompt_tokens || 0,  
            total_completion_tokens: user.total_completion_tokens || 0,
            text_generation: {
              cost: user.text_generation?.cost || 0,
              tokens: user.text_generation?.tokens || 0,
              pending: user.text_generation?.pending || false
            },
            image_generation: {
              cost: user.image_generation?.cost || 0,
              tokens: user.image_generation?.tokens || 0,
              pending: user.image_generation?.pending || false
            },
            total_cost: user.total_cost || 0,
            // Check if user has any pending records
            has_pending: (user.text_generation?.pending || user.image_generation?.pending) || false
          }));
          
          console.log('User Breakdown Data:', formattedUserBreakdown);
          console.log('Users with pending status:', formattedUserBreakdown.filter(user => user.has_pending));
          console.log('Should show refresh pending button:', formattedUserBreakdown.some(user => user.has_pending));
          setUserUsage(formattedUserBreakdown);
          
          // Update usage logs with recent transactions (simplified)
          setUsageLogs(formattedUserBreakdown.slice(0, 10).map((user, index) => ({
            id: `recent_${index}`,
            user: user.email || user.name,
            cost: user.total_cost,
            model: user.models_used?.[0] || 'Various',
            provider: user.providers_used?.[0] || 'Various',
            created_at: new Date().toISOString(),
            description: `${user.total_requests || 0} requests by ${user.name || 'Unknown User'}`
          })));
        } else {
          console.error('Analytics returned success=false:', analyticsData);
          throw new Error(analyticsData.error || 'Failed to load analytics data');
        }

      // Fallback: Fetch AI settings for compatibility
      const settingsResponse = await fetch(`${API_BASE_URL}/api/ai/settings?t=${timestamp}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json();
        setAiSettings(settingsData.settings);
      }

    } catch (error) {
      console.error('Error fetching OpenRouter cost data:', error);
      setMessage({ type: 'error', text: 'Failed to load real-time cost data from OpenRouter' });
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  };

  const handleRefreshClick = async () => {
    setShowRefreshModal(true);
    try {
      // Clear existing data first
      setAnalytics(null);
      setUserUsage([]);
      setUsageLogs([]);
      setMessage({ type: '', text: '' });
      
      // Add a small delay for better UX
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Fetch fresh data from OpenRouter
      await fetchCostData();
      
      // Show success message briefly
      setMessage({ 
        type: 'success', 
        text: 'Cost analytics data refreshed successfully' 
      });
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
      
    } catch (error) {
      console.error('Error during refresh:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to refresh data. Please try again.' 
      });
    } finally {
      setShowRefreshModal(false);
    }
  };

  const handleRetryPendingClick = async () => {
    setRefreshingPending(true);
    try {
      const timestamp = new Date().getTime();
      
      // Call the retry pending endpoint
      const retryResponse = await fetch(`${API_BASE_URL}/api/openrouter/retry-pending-costs?t=${timestamp}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (retryResponse.ok) {
        const retryData = await retryResponse.json();
        
        if (retryData.success) {
          setMessage({ 
            type: 'success', 
            text: `Successfully retried ${retryData.results.total_retried} pending records. ${retryData.results.successful_retrievals} were successful.`
          });
          
          // Refresh the data after retry
          await fetchCostData();
        } else {
          setMessage({ 
            type: 'error', 
            text: retryData.error || 'Failed to retry pending records' 
          });
        }
      } else {
        setMessage({ 
          type: 'error', 
          text: 'Failed to retry pending records. Please try again.' 
        });
      }
      
      // Hide message after 5 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
      
    } catch (error) {
      console.error('Error retrying pending records:', error);
      setMessage({ 
        type: 'error', 
        text: 'Network error while retrying pending records. Please try again.' 
      });
      
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
    } finally {
      setRefreshingPending(false);
    }
  };

  const purchaseCredits = async () => {
    setPurchasing(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/credits/purchase`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amount: purchaseAmount })
      });

      if (response.ok) {
        const data = await response.json();
        setMessage({ 
          type: 'success', 
          text: `Successfully purchased $${purchaseAmount} in credits. New balance: $${data.new_balance.toFixed(2)}` 
        });
        setShowPurchaseModal(false);
        await fetchCostData(); // Refresh data
      } else {
        const errorData = await response.json();
        setMessage({ 
          type: 'error', 
          text: errorData.detail || 'Failed to purchase credits' 
        });
      }
    } catch (error) {
      console.error('Error purchasing credits:', error);
      setMessage({ 
        type: 'error', 
        text: 'Network error. Please try again.' 
      });
    } finally {
      setPurchasing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0);
  };

  // Helper function to clean up prefixed user names
  const cleanUserName = (username) => {
    if (!username) return 'Unknown User';
    
    // Check if it has app prefix pattern (APPNAME_user_name)
    const parts = username.split('_');
    if (parts.length > 1) {
      // Remove the first part (app prefix) and rejoin the rest
      const cleanedName = parts.slice(1).join('_');
      // Convert underscores to spaces and title case
      return cleanedName
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
    }
    
    // If no prefix, just return cleaned version
    return username.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (authLoading || pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-theme-blue mx-auto mb-4" />
          <p className="text-gray-600">Loading cost analytics...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600">Please log in to view AI cost analytics.</p>
        </div>
      </div>
    );
  }

  // Calculate text vs image costs from analytics data instead of transaction logs
  const textCost = analytics?.text_generation_cost || 0;
  const imageCost = analytics?.image_generation_cost || 0;
  
  // Get image tokens from analytics data
  const imageTokens = analytics?.image_generation_tokens || 0;
  
  const totalCost = analytics?.total_cost || 0;

  // Use provider breakdown from analytics data
  const providerBreakdown = analytics?.provider_breakdown || [];

  // Use model breakdown from analytics data  
  const modelBreakdown = analytics?.model_breakdown || [];

  // Use analytics data directly (not nested in summary)
  const summary = {
    total_cost: totalCost,
    total_tokens: analytics?.total_tokens || 0,
    total_requests: analytics?.total_messages || 0,
    monthly_cost: analytics?.monthly_cost || 0,
    text_cost: textCost,
    image_cost: imageCost,
    image_tokens: imageTokens
  };
  
  const dailyTrends = analytics?.daily_trends || [];

  // Check if user has permission to access AI Costs
  if (!authLoading && !hasPageAccess(user, 'ai_features.costs', ['admin', 'owner'])) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access AI Costs.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center space-x-3">
                  <Activity className="h-8 w-8 text-green-600" />
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      AI Costs Analytics
                    </h2>
                  </div>
                </div>

                {/* Credit Balance and Purchase */}
                <div className="flex flex-row items-center space-x-3">
                  {analytics && (
                    <div className="flex items-center space-x-2 bg-green-50 px-3 py-2 rounded-lg">
                      <CreditCard className="h-4 w-4 text-green-600" />
                      <span className="font-medium text-green-800">
                        ${analytics.credits_balance?.toFixed(2) || '0.00'}
                      </span>
                      <span className="text-sm text-green-600">credits</span>
                    </div>
                  )}
                  
                  {isAdmin && (
                    <button
                      onClick={() => setShowPurchaseModal(true)}
                      className="text-white px-4 py-2 rounded-lg flex items-center space-x-2 text-sm"
                      style={{ backgroundColor: 'var(--theme-primary)' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-primary-hover)'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                    >
                      <Plus className="h-4 w-4" />
                      <span>Buy Credits</span>
                    </button>
                  )}

                  <div className="flex items-center space-x-2">
                    <Filter className="h-4 w-4 text-gray-500" />
                    <select
                      value={selectedPeriod}
                      onChange={(e) => setSelectedPeriod(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm min-w-0"
                    >
                      {periods.map(period => (
                        <option key={period.value} value={period.value}>
                          {period.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Only show Refresh Pending button when there are pending errors in Usage by User table */}
                  {(() => {
                    const hasPendingUsers = userUsage.some(user => user.has_pending);
                    console.log('Debug - Button condition check:', {
                      userUsageLength: userUsage.length,
                      hasPendingUsers,
                      userUsage: userUsage.map(u => ({ name: u.name, has_pending: u.has_pending }))
                    });
                    return hasPendingUsers;
                  })() && (
                    <button
                      onClick={handleRetryPendingClick}
                      disabled={refreshingPending}
                      className="px-4 py-2 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 flex items-center text-sm font-medium transition-colors flex-shrink-0"
                      style={{ backgroundColor: 'rgb(251, 146, 60)' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(245, 130, 32)'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(251, 146, 60)'}
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${refreshingPending ? 'animate-spin' : ''}`} />
                      Refresh Pending ({analytics.pending_404_count})
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {message.text && (
                <div className={`mb-6 p-4 rounded-lg border ${
                  message.type === 'error' 
                    ? 'bg-red-50 text-red-700 border-red-200' 
                    : 'bg-green-50 text-green-700 border-green-200'
                }`}>
                  <div className="flex items-center">
                    {message.type === 'error' ? (
                      <AlertTriangle className="h-4 w-4 mr-2" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    {message.text}
                  </div>
                </div>
              )}

              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <DollarSign className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Total Cost</p>
                      <p className="text-lg font-bold text-gray-900">{formatCurrency(summary.total_cost)}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <MessageSquare className="h-5 w-5 text-theme-blue" />
                    </div>
                    <div className="ml-3">
                      <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Text Generation</p>
                      <p className="text-lg font-bold text-gray-900">{formatCurrency(summary.text_cost)}</p>
                      <p className="text-xs text-gray-500">{formatNumber(summary.total_tokens)} tokens</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <ImageIcon className="h-5 w-5 text-purple-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Image Generation</p>
                      <p className="text-lg font-bold text-gray-900">{formatCurrency(summary.image_cost)}</p>
                      <p className="text-xs text-gray-500">{formatNumber(analytics?.image_generation_tokens || 0)} tokens</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Activity className="h-5 w-5 text-orange-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Total Requests</p>
                      <p className="text-lg font-bold text-gray-900">{formatNumber(summary.total_requests)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Provider Breakdown */}
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                    <PieChart className="h-5 w-5 mr-2 text-green-600" />
                    Cost by Provider
                  </h3>
                  <div className="space-y-3">
                    {providerBreakdown.map((provider, index) => (
                      <div key={provider._id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100">
                        <div className="flex items-center">
                          <div className={`w-3 h-3 rounded-full mr-3 ${
                            index === 0 ? 'bg-theme-blue' : index === 1 ? 'bg-green-500' : index === 2 ? 'bg-purple-500' : 'bg-orange-500'
                          }`}></div>
                          <span className="text-sm font-medium text-gray-700 capitalize">{provider._id}</span>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-gray-900">{formatCurrency(provider.cost)}</p>
                          <p className="text-xs text-gray-500">{provider.requests} requests</p>
                        </div>
                      </div>
                    ))}
                    {providerBreakdown.length === 0 && (
                      <div className="text-center py-8">
                        <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 text-sm">No data available for the selected period</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Model Breakdown */}
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2 text-green-600" />
                    Cost by Model
                  </h3>
                  <div className="space-y-3">
                    {modelBreakdown.map((model, index) => (
                      <div key={model._id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100">
                        <div className="flex items-center">
                          <div className={`w-3 h-3 rounded-full mr-3 ${
                            index === 0 ? 'bg-indigo-500' : index === 1 ? 'bg-pink-500' : index === 2 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}></div>
                          <span className="text-sm font-medium text-gray-700">{model._id}</span>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-gray-900">{formatCurrency(model.cost)}</p>
                          <p className="text-xs text-gray-500">{model.requests} requests</p>
                        </div>
                      </div>
                    ))}
                    {modelBreakdown.length === 0 && (
                      <div className="text-center py-8">
                        <PieChart className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 text-sm">No data available for the selected period</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Usage By User Table */}
              <div className="bg-gray-50 rounded-lg border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200 bg-white rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900 flex items-center">
                      <Users className="h-5 w-5 mr-2 text-green-600" />
                      Usage By User
                    </h3>
                    
                    {/* Refresh Pending button moved to top header */}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-100 border-b border-gray-200">
                      <tr>
                        <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">User</th>
                        <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Total Tokens</th>
                        <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Text Generation</th>
                        <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Image Generation</th>
                        <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Total Cost</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {userUsage.map((user, index) => (
                        <tr key={user.user_id || index} className="hover:bg-gray-50 transition-colors">
                          {/* User Column */}
                          <td className="px-3 sm:px-4 py-4">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8">
                                <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                                  <span className="text-xs font-medium text-gray-700">
                                    {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-3">
                                <p className="text-sm font-medium text-gray-900">
                                  {user.name || 'Unknown User'}
                                </p>
                              </div>
                            </div>
                          </td>
                          
                          {/* Total Tokens Column */}
                          <td className="px-3 sm:px-4 py-4">
                            <div className="text-sm text-gray-900">
                              <div className="font-medium">
                                {formatNumber(user.total_tokens || 0)}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                Prompt: {formatNumber(user.total_prompt_tokens || 0)} • Completion: {formatNumber(user.total_completion_tokens || 0)}
                              </div>
                            </div>
                          </td>
                          
                          {/* Text Generation Column */}
                          <td className="px-3 sm:px-4 py-4">
                            <div className="text-sm text-gray-900">
                              <div className="font-medium">
                                {formatCurrency(user.text_generation?.cost || 0)}
                                {user.text_generation?.pending && (
                                  <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                                    Pending
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {formatNumber(user.text_generation?.tokens || 0)} tokens
                                {user.text_generation?.pending && (
                                  <span className="text-yellow-600"> (Updating...)</span>
                                )}
                              </div>
                            </div>
                          </td>
                          
                          {/* Image Generation Column */}
                          <td className="px-3 sm:px-4 py-4">
                            <div className="text-sm text-gray-900">
                              <div className="font-medium">
                                {formatCurrency(user.image_generation?.cost || 0)}
                                {user.image_generation?.pending && (
                                  <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                                    Pending
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {formatNumber(user.image_generation?.tokens || 0)} tokens
                                {user.image_generation?.pending && (
                                  <span className="text-yellow-600"> (Updating...)</span>
                                )}
                              </div>
                            </div>
                          </td>
                          
                          {/* Total Cost Column */}
                          <td className="px-3 sm:px-4 py-4 text-sm font-semibold text-gray-900">
                            {formatCurrency(user.total_cost || 0)}
                          </td>
                        </tr>
                      ))}
                      {userUsage.length === 0 && (
                        <tr>
                          <td colSpan="5" className="px-6 py-12 text-center">
                            <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 text-sm">No user usage data available for the selected period</p>
                            <p className="text-gray-400 text-xs mt-1">Data will appear here when users interact with AI services</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Purchase Credits Modal */}
      {showPurchaseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Purchase OpenRouter Credits</h3>
              <button
                onClick={() => setShowPurchaseModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Current Balance: <span className="font-medium text-green-600">${analytics?.credits_balance?.toFixed(2) || '0.00'}</span>
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Purchase Amount ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  max="500"
                  value={purchaseAmount}
                  onChange={(e) => setPurchaseAmount(parseFloat(e.target.value) || 10)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-theme-blue focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Minimum: $1.00 • Maximum: $500.00
                </p>
              </div>

              {/* Quick Amount Buttons */}
              <div className="mb-6">
                <p className="text-sm font-medium text-gray-700 mb-2">Quick Select</p>
                <div className="grid grid-cols-4 gap-2">
                  {[10, 25, 50, 100].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setPurchaseAmount(amount)}
                      className={`px-3 py-2 text-sm rounded border ${
                        purchaseAmount === amount
                          ? 'bg-theme-blue text-white border-theme-blue'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-6">
                <p className="text-sm text-yellow-800">
                  <span className="font-medium">Note:</span> This is a demo purchase. In production, this would integrate with Stripe for real payments.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowPurchaseModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={purchaseCredits}
                disabled={purchasing || purchaseAmount < 1}
                className="px-4 py-2 text-sm font-medium text-white bg-theme-blue border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {purchasing ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Purchase ${purchaseAmount}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refreshing Modal */}
      {showRefreshModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
            <div className="p-8 text-center">
              <div className="mx-auto mb-4">
                <RefreshCw className="h-12 w-12 animate-spin text-theme-blue mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Refreshing...</h3>
              <p className="text-sm text-gray-600">
                Fetching latest cost analytics data
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AICosts;